import { createResponseSchema } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  MdmRequestFilterParams,
  RequestFilterParams,
} from './filterOptions.js';
import { MdmAttributeValueSchema } from './mdmAttribute.js';
import { PricingItemSchemaMdm, PricingItemSchemaOld } from './pricingItem.js';
import { LevelType, PlacementFolderOrdering } from './shared.js';

// TODO(ghostMdm): remove legacy `PlacementFolderSchemaOld` with `ff.ghostMdm.enabled`.
export const PlacementFolderSchemaOld = z.object({
  id: z.string(),
  hasChildren: z.boolean(),
  levelType: z.literal(LevelType.PLACEMENT_FOLDER),
  attributes: z.object({
    name: z.string().nullable(),
    userDefinedId: z.string().nullable(),
    dateFrom: z.string(),
    dateTo: z.string(),
    priceType: z.string().nullable(),
    price: z.string().nullable(),
  }),
});

export const PlacementFolderSchemaMdm = z.object({
  id: z.string(),
  hasChildren: z.boolean(),
  levelType: z.literal(LevelType.PLACEMENT_FOLDER),
  attributes: z.record(z.string(), MdmAttributeValueSchema),
});

export const GetPlacementFoldersResponseSchemaOld = createResponseSchema(
  PlacementFolderSchemaOld.or(PricingItemSchemaOld),
);

export const GetPlacementFoldersResponseSchemaMdm = createResponseSchema(
  PlacementFolderSchemaMdm.or(PricingItemSchemaMdm),
);

export const GetPlacementFoldersRequestSchema = z.object({
  startRow: z.number(),
  endRow: z.number(),
  levelType: z.enum(LevelType),
  parentId: z.string(),
  ordering: z.enum(PlacementFolderOrdering).nullable().optional(),
  filters: RequestFilterParams.nullable(),
});

export const MdmOrderingSchema = z.object({
  urn: z.string(),
  asc: z.boolean(),
});

export const GetPlacementFoldersRequestSchemaMdm = z.object({
  startRow: z.number(),
  endRow: z.number(),
  levelType: z.enum(LevelType),
  parentId: z.string(),
  ordering: MdmOrderingSchema.nullable().optional(),
  filters: MdmRequestFilterParams.nullable(),
});

export const CreateFolderRequestSchema = z.object({
  virtualDspCampaignId: z.string(),
  itemsNum: z.number(),
});

export const CreateFolderResponseSchema = createResponseSchema(
  z.object({
    id: z.string(),
    name: z.string().nullable(),
    dateFrom: z.string(),
    dateTo: z.string(),
    price: z.string().nullable(),
    priceType: z.string().nullable(),
    userDefinedId: z.string().nullable(),
  }),
);

const UpdateDataSchema = z.record(z.string(), z.string().nullable());
//TODO: remove with `ff.ghostMdm.enabled`
const UpdateDataSchemaOld = z.object({
  name: z.string().nullable().optional(),
  dateFrom: z.string().nullable().optional(),
  dateTo: z.string().nullable().optional(),
  price: z.string().nullable().optional(),
  priceType: z.string().nullable().optional(),
  userDefinedId: z.string().nullable().optional(),
});
export const BulkUpdateFolderRequestSchema = z.object({
  virtualDspCampaignId: z.string(),
  updateData: UpdateDataSchema.or(UpdateDataSchemaOld),
  placementFolderIds: z.array(z.string()),
  selectAll: z.boolean(),
  excludeIds: z.array(z.string()),
  filters: RequestFilterParams.nullable(),
});

export const BulkUpdateFolderResponseSchema = z.object({
  data: z.null(),
  error: z.null(),
});

export const BulkUpdateFolderRequestSchemaMdm = z.object({
  virtualDspCampaignId: z.string(),
  placementFolderIds: z.array(z.string()),
  selectAll: z.boolean(),
  excludeIds: z.array(z.string()),
  filters: MdmRequestFilterParams.nullable(),
  attributes: z.record(z.string(), MdmAttributeValueSchema.shape.value),
});

export const DuplicateFolderRequestSchema = z.object({
  placementFolderId: z.string(),
  copiesNum: z.number(),
});

export const DuplicateFolderResponseSchema = z.object({
  data: z.null(),
  error: z.null(),
});

export const BulkDeleteFolderRequestSchema = z.object({
  virtualDspCampaignId: z.string(),
  placementFolderIds: z.array(z.string()),
  selectAll: z.boolean(),
  excludeIds: z.array(z.string()),
  filters: RequestFilterParams.or(MdmRequestFilterParams).nullable(),
});

export const MultiUpdatePlacementFoldersRowSchema = z
  .object({
    id: z.string(),
  })
  .catchall(MdmAttributeValueSchema.shape.value);

export const MultiUpdatePlacementFoldersRequestSchema = z.object({
  virtualDspCampaignId: z.string(),
  placementFolders: z.array(MultiUpdatePlacementFoldersRowSchema),
  pricingItem: z.array(MultiUpdatePlacementFoldersRowSchema),
});

export const MultiUpdatePlacementFoldersResponseSchema = z.object({
  data: z.null(),
  error: z.null(),
});
